import { Route, RouteGroup, TripPattern } from "@/entities";
import { TreeDto } from "@/helpers/interfaces";
import { TreeType } from "@/helpers/types";
import { getCheckpointByName, getRouteByName, getTripPatternByName } from "../routes/route_group.format";
import { getActiveCheckpoints, getActiveRoutes, getActiveTripPatterns, getCheckpointTree, getRouteTree, getTripPatternTree } from "./tree.format";
import { MapLine } from "@/ui/components/maps/common/interfaces/map-line";
import { MapCoordinate } from "@/ui/components/maps/common/interfaces/map-coordinate";
import { MapCheckpoint } from "@/ui/components/maps/common/interfaces/map-checkpoint";

export const formatTreeRouteGroup = (routeGroups: RouteGroup[]) => {
    return routeGroups.map((rg) => {
        const routeTrees = rg.routes.map(r => {
            const tripPatternTrees = r.tripPattern.map(tp => {
                const checkpointTrees = tp.trip_pattern_checkpoints.map(tpc => {
                    const checkpointTree: TreeDto = {
                        label: tpc.checkpoint.name,
                        type: TreeType.CHECKPOINT,
                        id: tpc.checkpoint.id,
                        is_active_tracking: false,
                        is_active_route: false,
                        is_active_checkpoint: false
                    }
                    return checkpointTree;
                });

                const tripPatternTree: TreeDto = {
                    label: tp.name,
                    type: TreeType.TRIP_PATTERN,
                    children: checkpointTrees,
                    id: tp.id,
                    is_active_tracking: false,
                    is_active_route: false,
                    is_active_checkpoint: false
                }

                return tripPatternTree;
            });

            const routeTree: TreeDto = {
                label: r.long_name,
                type: TreeType.ROUTE,
                id: r.id,
                is_active_tracking: false,
                children: tripPatternTrees,
                is_active_route: false,
                is_active_checkpoint: false
            }

            return routeTree;
        });

        const routeGroupTree: TreeDto = {
            id: rg.id,
            label: rg.name,
            type: TreeType.ROUTE_GROUP,
            is_active_tracking: false,
            is_active_route: false,
            is_active_checkpoint: false,
            children: routeTrees
        }

        return routeGroupTree;
    });
}

const formatMapLineTripPattern = (tripPattern: TripPattern, color?: string): MapLine => {
    const mapCoordinates = tripPattern.plotted[0].coords.map(x => {
        const mapCoordinate: MapCoordinate = {
            name: tripPattern.name,
            coordinate: [x.longitude, x.latitude],
            isPrimaryColor: true,
        }

        return mapCoordinate;
    });

    const mapLine: MapLine = {
        identification: tripPattern.id + '-' + tripPattern.name,
        color: color,
        coordinates: mapCoordinates
    }

    return mapLine;
}

const formatMapLineRoute = (route: Route): MapLine[] => {
    return route.tripPattern.map(tp => {
        const mapCoordinates = tp.plotted[0].coords.map(x => {
            const mapCoordinate: MapCoordinate = {
                name: route.long_name + '-' + tp.name,
                coordinate: [x.longitude, x.latitude],
                isPrimaryColor: true,
            }

            return mapCoordinate;
        });

        const mapLine: MapLine = {
            identification: tp.id + '-' + tp.name,
            color: route.color.outline,
            coordinates: mapCoordinates
        }

        return mapLine;
    });
}

export const formatMapLineRouteGroup = (routeGroups: RouteGroup[], routeGroupTrees: TreeDto[], name: string, type: TreeType, state: boolean) => {
    const mapLines: MapLine[] = [];

    switch (type) {
        case TreeType.ROUTE_GROUP: {
            const routeGroupTreesFormat = routeGroupTrees.filter(x => x.is_active_route);
            for (const routeGroupTree of routeGroupTreesFormat) {
                const routeGroup = routeGroups.find((rg) => rg.name === routeGroupTree.label);
                if (routeGroup) {
                    routeGroup.routes.map(r => {
                        const routeLines = formatMapLineRoute(r);
                        routeLines.map(x => mapLines.push(x));
                    });
                }
            }

            return mapLines;
        }
        case TreeType.ROUTE: {
            const routeTrees = getActiveRoutes(routeGroupTrees, 'route');
            for (const routeTree of routeTrees) {
                const route = getRouteByName(routeGroups, routeTree.label);
                if (route) {
                    const routeLines = formatMapLineRoute(route);
                    routeLines.map(x => mapLines.push(x));
                }
            }
            break;
        }
        case TreeType.TRIP_PATTERN: {
            const tripPatternTrees = getActiveTripPatterns(routeGroupTrees, 'route');
            for (const tripPatternTree of tripPatternTrees) {
                const tripPattern = getTripPatternByName(routeGroups, tripPatternTree.label);
                if (tripPattern) {
                    const tripPatternLine = formatMapLineTripPattern(tripPattern);
                    mapLines.push(tripPatternLine);
                }
            }
            break;
        }
        default:
            break;
    }

    return mapLines;
}


const formatMapCoordsTripPattern = (tripPattern: TripPattern): MapCheckpoint[] => {
    const mapCoords: MapCheckpoint[] = [];

    tripPattern.trip_pattern_checkpoints.map(tpc => {
        const mapCoordinate: MapCheckpoint = {
            name: tpc.checkpoint.name,
            coordinate: [tpc.checkpoint.longitude, tpc.checkpoint.latitude],
            isPrimaryColor: true,
            radio: tpc.checkpoint.radio
        }

        mapCoords.push(mapCoordinate);
    });


    return mapCoords;
}

const formatMapCoordsRoute = (route: Route): MapCoordinate[] => {
    const mapCoords: MapCoordinate[] = [];
    route.tripPattern.map(tp => {
        const tripPatternCoords = formatMapCoordsTripPattern(tp);
        tripPatternCoords.map(x => mapCoords.push(x));
    });

    return mapCoords;
}


export const formatPointRouteGroup = (routeGroups: RouteGroup[], routeGroupTrees: TreeDto[], name: string, type: TreeType, state: boolean) => {
    const mapCoords: MapCheckpoint[] = [];
    switch (type) {
        case TreeType.ROUTE_GROUP: {
            const routeGroupTreesFormat = routeGroupTrees.filter(x => x.is_active_checkpoint);
            for (const routeGroupTree of routeGroupTreesFormat) {
                const routeGroup = routeGroups.find((rg) => rg.name === routeGroupTree.label);
                if (routeGroup) {
                    routeGroup.routes.map(r => {
                        const checkpoints = formatMapCoordsRoute(r);
                        checkpoints.map(x => mapCoords.push(x));
                    });
                }
            }
            return mapCoords;
        }
        case TreeType.ROUTE: {
            const routeTrees = getActiveRoutes(routeGroupTrees, 'checkpoint');
            for (const routeTree of routeTrees) {
                const route = getRouteByName(routeGroups, routeTree.label);
                if (route) {
                    const routeCoords = formatMapCoordsRoute(route);
                    routeCoords.map(x => mapCoords.push(x));
                }
            }
            break;
        }
        case TreeType.TRIP_PATTERN: {
            const tripPatternTrees = getActiveTripPatterns(routeGroupTrees, 'checkpoint');
            for (const tripPatternTree of tripPatternTrees) {
                const tripPattern = getTripPatternByName(routeGroups, tripPatternTree.label);
                if (tripPattern) {
                    const tripPatternCoords = formatMapCoordsTripPattern(tripPattern);
                    tripPatternCoords.map(x => mapCoords.push(x));
                }
            }
            break;
        }
        case TreeType.CHECKPOINT: {
            const checkpointTrees = getActiveCheckpoints(routeGroupTrees, 'checkpoint');
            for (const checkpointTree of checkpointTrees) {
                const checkpoint = getCheckpointByName(routeGroups, checkpointTree.label);
                if (checkpoint) {
                    const checkpointCoord: MapCheckpoint = {
                        name: checkpoint.checkpoint.name,
                        coordinate: [
                            checkpoint.checkpoint.longitude,
                            checkpoint.checkpoint.latitude
                        ],
                        isPrimaryColor: true,
                        radio: checkpoint.checkpoint.radio
                    }
                    mapCoords.push(checkpointCoord);
                }
            }
            break;
        }
        default:
            break;
    }

    return mapCoords;
}



const setActiveNode = (nodes: TreeDto[], type: string, isActive = true) => {
    if (nodes.length > 0) {
        nodes.map(x => {
            switch (type) {
                case 'route':
                    x.is_active_route = isActive;
                    break;
                case 'checkpoint':
                    x.is_active_checkpoint = isActive;
                    break;
                case 'tracking':
                    x.is_active_tracking = isActive;
                    break;
                default:
                    break;
            }

            if (x.children && x.children.length > 0) {
                setActiveNode(x.children, type, isActive);
            }
        });
    }
}

export const setActiveItem = (nodes: TreeDto[], nodeActive: TreeDto, origin: string) => {
    let response_route = false;
    let response_tracking = false;
    let response_checkpoint = false;

    switch (nodeActive.type) {
        case TreeType.ROUTE_GROUP: {
            for (const node of nodes) {
                if (node.label == nodeActive.label) {
                    if (origin == 'route') {
                        node.is_active_route = !node.is_active_route;
                        if (node.children && node.children.length > 0) {
                            setActiveNode(node.children, 'route', node.is_active_route);
                        }
                        response_route = node.is_active_route;
                    }

                    if (origin == 'tracking') {
                        node.is_active_tracking = !node.is_active_tracking;
                        if (node.children && node.children.length > 0) {
                            setActiveNode(node.children, 'tracking', node.is_active_tracking);
                        }
                        response_tracking = node.is_active_tracking;
                    }

                    if (origin == 'checkpoint') {
                        node.is_active_checkpoint = !node.is_active_checkpoint;
                        if (node.children && node.children.length > 0) {
                            setActiveNode(node.children, 'checkpoint', node.is_active_checkpoint);
                        }
                        response_checkpoint = node.is_active_checkpoint;
                    }
                }
            }
            break;
        }
        case TreeType.ROUTE: {
            const routeTree = getRouteTree(nodes, nodeActive.label);
            if (routeTree && origin == 'route') {
                routeTree.is_active_route = !routeTree.is_active_route;
                response_route = routeTree.is_active_route;
                if (routeTree.children && routeTree.children.length > 0) {
                    setActiveNode(routeTree.children, 'route', routeTree.is_active_route);
                }
            }

            if (routeTree && origin == 'tracking') {
                routeTree.is_active_tracking = !routeTree.is_active_tracking;
                response_tracking = routeTree.is_active_tracking;
                if (routeTree.children && routeTree.children.length > 0) {
                    setActiveNode(routeTree.children, 'tracking', routeTree.is_active_tracking);
                }
            }

            if (routeTree && origin == 'checkpoint') {
                routeTree.is_active_checkpoint = !routeTree.is_active_checkpoint;
                response_checkpoint = routeTree.is_active_checkpoint;
                if (routeTree.children && routeTree.children.length > 0) {
                    setActiveNode(routeTree.children, 'checkpoint', routeTree.is_active_checkpoint);
                }
            }
            break;
        }
        case TreeType.TRIP_PATTERN: {
            const tripPatternTree = getTripPatternTree(nodes, nodeActive.label);
            if (tripPatternTree && origin == 'route') {
                tripPatternTree.is_active_route = !tripPatternTree.is_active_route;
                response_route = tripPatternTree.is_active_route;
                if (tripPatternTree.children && tripPatternTree.children.length > 0) {
                    setActiveNode(tripPatternTree.children, 'route', tripPatternTree.is_active_route);
                }
            }

            if (tripPatternTree && origin == 'tracking') {
                tripPatternTree.is_active_tracking = !tripPatternTree.is_active_tracking;
                response_tracking = tripPatternTree.is_active_tracking;
                if (tripPatternTree.children && tripPatternTree.children.length > 0) {
                    setActiveNode(tripPatternTree.children, 'tracking', tripPatternTree.is_active_tracking);
                }
            }

            if (tripPatternTree && origin == 'checkpoint') {
                tripPatternTree.is_active_checkpoint = !tripPatternTree.is_active_checkpoint;
                response_checkpoint = tripPatternTree.is_active_checkpoint;
                if (tripPatternTree.children && tripPatternTree.children.length > 0) {
                    setActiveNode(tripPatternTree.children, 'checkpoint', tripPatternTree.is_active_checkpoint);
                }
            }
            break;
        }
        case TreeType.CHECKPOINT: {
            const checkpointTree = getCheckpointTree(nodes, nodeActive.label);
            if (checkpointTree && origin == 'route') {
                checkpointTree.is_active_route = !checkpointTree.is_active_route;
                response_route = checkpointTree.is_active_route;
            }

            if (checkpointTree && origin == 'tracking') {
                checkpointTree.is_active_tracking = !checkpointTree.is_active_tracking;
                response_tracking = checkpointTree.is_active_tracking;
            }

            if (checkpointTree && origin == 'checkpoint') {
                checkpointTree.is_active_checkpoint = !checkpointTree.is_active_checkpoint;
                response_checkpoint = checkpointTree.is_active_checkpoint;
            }
            break;
        }
        default:
            break;
    }

    return { response_route, response_tracking, response_checkpoint, origin };
}